import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './GlobalSearchSlideout.module.scss';

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'deals', label: 'Deals' },
  { key: 'documents', label: 'Documents' },
  { key: 'templates', label: 'Templates' },
  { key: 'clauses', label: 'Clauses' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'checklists', label: 'Checklists' },
];

export const DOCUMENT_FORMATS = [
  { key: 'word', label: 'Word documents' },
  { key: 'pdf', label: 'PDFs' },
];

interface CategoryPillsProps {
  activeCategory: string;
  categoryCounts: Record<string, number>;
  onCategoryChange: (category: string) => void;
  selectedFormats: string[] | null;
  onFormatChange: (formats: string[] | null) => void;
}

export const CategoryPills: React.FC<CategoryPillsProps> = ({
  activeCategory,
  categoryCounts,
  onCategoryChange,
  selectedFormats,
  onFormatChange,
}) => {
  const [isFormatOpen, setIsFormatOpen] = useState(false);
  const documentsRef = useRef<HTMLDivElement>(null);

  const totalCount = Object.values(categoryCounts).reduce((sum, n) => sum + n, 0);
  const formatActive = selectedFormats !== null && selectedFormats.length > 0;

  useEffect(() => {
    if (!isFormatOpen) return;

    const handleClick = (e: MouseEvent) => {
      if (documentsRef.current && !documentsRef.current.contains(e.target as Node)) {
        setIsFormatOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isFormatOpen]);

  // Selecting every format is the same as selecting none, so it collapses back to
  // null and the pill stops reading as filtered.
  const toggleFormat = (key: string) => {
    const current = selectedFormats ?? DOCUMENT_FORMATS.map((f) => f.key);
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    onFormatChange(next.length === 0 || next.length === DOCUMENT_FORMATS.length ? null : next);
  };

  return (
    <>
      {CATEGORIES.map(({ key, label }) => {
        const count = key === 'all' ? totalCount : (categoryCounts[key] ?? 0);
        const isActive = activeCategory === key;

        // Format is a property of documents, so it belongs on the Documents pill
        // rather than in a filter of its own that would sit there meaninglessly
        // while another category is in view.
        if (key === 'documents') {
          return (
            <div key={key} className={styles.pillWithMenu} ref={documentsRef}>
              <button
                className={classNames(styles.pill, {
                  [styles.pill__active]: isActive,
                  [styles.pill__filtered]: formatActive,
                })}
                onClick={() => {
                  onCategoryChange(key);
                  setIsFormatOpen((open) => !open);
                }}
                aria-expanded={isFormatOpen}
                aria-haspopup="true"
              >
                {label}
                {count > 0 && <span className={styles.pillCount}>({count})</span>}
                <span className={styles.pillCaret} aria-hidden="true">▾</span>
              </button>

              {isFormatOpen && (
                <div className={styles.filterDropdown} role="group" aria-label="Document formats">
                  {DOCUMENT_FORMATS.map(({ key: formatKey, label: formatLabel }) => (
                    <label key={formatKey} className={styles.filterOption}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedFormats?.includes(formatKey) ?? true}
                        onChange={() => toggleFormat(formatKey)}
                      />
                      {formatLabel}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        }

        return (
          <button
            key={key}
            className={classNames(styles.pill, { [styles.pill__active]: isActive })}
            onClick={() => {
              onCategoryChange(key);
              setIsFormatOpen(false);
            }}
          >
            {label}
            {count > 0 && <span className={styles.pillCount}>({count})</span>}
          </button>
        );
      })}
    </>
  );
};
